import type { UseField } from '@seek/forms-ui';
import { minLength, required } from '@seek/validators-js';
import {
  Dropdown,
  Notice,
  PasswordField,
  Stack,
  Text,
} from 'braid-design-system';
import { useState } from 'react';

import { mandatoryLabel } from '#src/components/MandatoryLabel/MandatoryLabel.tsx';

const signingAlgorithmCodes = [
  { code: 'None', label: 'None (Unsigned)' },
  { code: 'SeekHmacSha512', label: 'HMAC-SHA512' },
];

export interface SigningFormData {
  signingAlgorithmCode?: string;
  secret?: string;
}

interface SigningFormProps<T extends SigningFormData> {
  disabled: boolean;
  mode: 'create' | 'update';
  useField: UseField<T>;
}
export const SigningForm = <T extends SigningFormData>({
  disabled,
  mode,
  useField,
}: SigningFormProps<T>) => {
  const signingAlgorithmCode = useField({
    id: 'signingAlgorithmCode',
    validators: [required],
  });

  const secret = useField({
    id: 'secret',
    validators: [minLength],
    validatorProps: {
      min: 16,
    },
  });

  const [disruptiveChange, setDisruptiveChange] = useState<boolean>(false);

  return (
    <Stack space="large">
      <Dropdown
        {...signingAlgorithmCode}
        {...mandatoryLabel('Signing algorithm')}
        description="Algorithm for signing webhook payloads to verify that the requests you’re receiving are coming from SEEK."
        id="webhookSubscriptionSigningAlgorithmCode"
        placeholder="Select signing algorithm..."
        tone={signingAlgorithmCode.valid ? 'neutral' : 'critical'}
        message={signingAlgorithmCode.valid ? undefined : 'Required field'}
        disabled
        value={signingAlgorithmCode.value ?? ''}
        onChange={(changeEvent) => {
          setDisruptiveChange(true);
          signingAlgorithmCode.onChange(changeEvent);
        }}
      >
        {signingAlgorithmCodes.map(({ code, label }) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </Dropdown>

      <PasswordField
        {...mandatoryLabel('Secret')}
        description="The secret that we’ll sign webhook requests with. If you’ve selected a signing algorithm above, you must provide a secret string here."
        id="webhookSubscriptionSecret"
        value={secret.value ?? ''}
        onChange={(changeEvent) => {
          setDisruptiveChange(true);
          secret.onChange(changeEvent);
        }}
        onBlur={secret.onBlur}
        disabled={disabled}
        tone={secret.valid ? 'neutral' : 'critical'}
        message={secret.errorMessage}
      />

      {mode === 'update' && disruptiveChange ? (
        <Notice>
          <Text>
            Make sure that your software is ready to receive incoming requests
            with this signing configuration to avoid disruption.
          </Text>
        </Notice>
      ) : null}
    </Stack>
  );
};
